/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { en_SourceType, zh_SourceType } from '../const';
import GrammarEN from '../grammar/en-US';
import { IgnoreCaseMatch, mapTypeChinese } from '../grammar/gammar';
import GrammarZH from '../grammar/zh-CN';
import { $$default, isBooleanGrammar, isEnumGrammar, isPerGrammar } from './grammar.type';
import { add, findMatchEnum, findMatchGrammar } from './grammar.util';
import { parse } from './parse';
import { tokenizer } from './tokenizer';

const generateGrammarList = (grammar): any => {
    const sourceType = Object.keys(grammar);
    const Grammar = {};
    for (const type of sourceType) {
        add(Grammar, type, grammar[type]);
    }
    return Grammar;
};

const zhGrammarList = generateGrammarList(GrammarZH);
const enGrammarList = generateGrammarList(GrammarEN);

const TypeMap = {
    LUN: 'LUNs',
    LUN组: 'LUNGroups',
    Namespace: 'Namespaces',
    Namespace组: 'NamespaceGroups',
    主机: 'Hosts',
    主机组: 'HostGroups',
    端口组: 'PortGroups',
    逻辑端口: 'LogicalPorts',
    多租户: 'vStores',
    LUN快照: 'LUNSnapshots',
    文件系统: 'FileSystems',
};
const zhReg = new RegExp(`^(${zh_SourceType.join('|')})\\s`, 'i');
const enReg = new RegExp(`^(${en_SourceType.join('|')})\\s`, 'i');
const getMatchEnumValue = (val, matchGrammar): any => {
    const value = val.value;
    const matchEnum = findMatchEnum(val, matchGrammar.enumValue).find((i) => i.isCompleteMatch);
    return matchEnum ? matchEnum[matchEnum[value]] || matchEnum[value] : undefined;
};
const fromParam = (param, grammarList, goalGramList, isEN): any => {
    const { key, symbol, value: val } = param;
    const value = val ? val.value : undefined;
    let completeMatch = findMatchGrammar(key, grammarList).find((i) => i.isCompleteMatch);
    let reverse = false;
    if (isEN && !completeMatch) {
        reverse = true;
        completeMatch = findMatchGrammar(key, goalGramList).find((i) => i.isCompleteMatch);
    }
    const symbolStr = symbol && symbol !== $$default ? ' ' + symbol : '';
    // 无法查找到匹配语法
    if (!completeMatch) {
        return `${key}${symbolStr} ${value || ''}`;
    }
    const { matchItem: matchGrammar } = completeMatch;
    const keyValue = matchGrammar.value;
    const goalMatchGrammar = goalGramList.find((i) => i.value === keyValue);
    // 非枚举语法翻译到key
    if (!isEnumGrammar(matchGrammar) && !isBooleanGrammar(matchGrammar) && !isPerGrammar(matchGrammar) || !value) {
        return `${goalMatchGrammar.key}${symbolStr} ${value || ''}`;
    }
    // 枚举语法需要翻译枚举值
    let newValue;
    let goalMatchValue = getMatchEnumValue(val, matchGrammar);
    if (!goalMatchValue && isEN) {
        const use = reverse ? grammarList : goalGramList;
        const useGram = use.find((i) => i.value === matchGrammar.value);
        goalMatchValue = getMatchEnumValue(val, useGram);
    }
    // 如果枚举值不存在，返回
    if (!goalMatchValue) {
        return `${goalMatchGrammar.key}${symbolStr} ${value}`;
    }
    const enumValueArray: Array<[string, string]> = Array.from(goalMatchGrammar.enumValue.entries());
    for (const [label, enumValue] of enumValueArray) {
        if (enumValue === goalMatchValue) {
            newValue = label;
            break;
        }
    }
    return `${goalMatchGrammar.key}${symbolStr} ${newValue}`;
};
/**
 * 翻译
 * @param input 需要转换的字符串
 * @param language  当前语言版本
 */
export const translate = (input: string, language: string): string => {
    const testReg = language === 'zh' ? enReg : zhReg;
    // 用于转换的语言版本
    const cur = language === 'zh' ? enGrammarList : zhGrammarList;
    // 目标语言版本
    const goal = language === 'zh' ? zhGrammarList : enGrammarList;
    if (testReg.test(input)) {
        const tokens = tokenizer(input, false);
        const AST = parse(tokens, false);
        // 获取对应类型的语法列表和内部使用的类型
        const [grammarList, innerType] = IgnoreCaseMatch(TypeMap[AST.type] || AST.type, cur);
        const outType = language === 'zh' ? mapTypeChinese(innerType) : innerType;
        // 根据key值查找匹配的语法列表
        const re = AST.params.map(
            (param) => fromParam(param, grammarList, goal[innerType], language === 'en'))
            .reduce((pre, next) => pre + ' ' + next, '');
        return outType + re;
    }

    return input;
};

